import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom"; // Added useNavigate

const API_BASE = process.env.REACT_APP_API_BASE_URL;

export default function AddQuestionsPage() {
  const { quizId } = useParams();
  const navigate = useNavigate(); // Added navigation hook
  const [question, setQuestion] = useState({
    questionText: "",
    options: ["", "", "", ""],
    correctAnswer: "",
    explanation: "",
  });
  const [count, setCount] = useState(0);
  type Question = {
    id: string;
    questionText: string;
    options: string[];
    correctAnswer: string;
    explanation?: string;
  };

  const [existingQuestions, setExistingQuestions] = useState<Question[]>([]);

  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        const res = await fetch(
          `${API_BASE}/api/quizzes/mentor/${quizId}/questions`,
          {
            credentials: "include",
          }
        );
        if (!res.ok) throw new Error("Failed to load questions");
        const data = await res.json();
        setExistingQuestions(data);
        setCount(data.length);
      } catch (err) {
        console.error("Error loading questions:", err);
      }
    };
    fetchQuestions();
  }, [quizId]);

  // Added cancel handler
  const handleCancel = () => {
    navigate("/admin/manage-quizzes");
  };

  const handleOptionChange = (idx: number, value: string) => {
    setQuestion((q) => {
      const opts = [...q.options];
      opts[idx] = value;
      return { ...q, options: opts };
    });
  };

  const handleAdd = async (e: { preventDefault: () => void }) => {
    e.preventDefault();
    try {
      const res = await fetch(
        `${API_BASE}/api/quizzes/mentor/${quizId}/questions`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            questionText: question.questionText,
            options: question.options,
            correctAnswer: question.correctAnswer,
            explanation: question.explanation,
          }),
        }
      );

      if (!res.ok) throw new Error("Failed to add question");

      setCount((c) => c + 1);
      setQuestion({
        questionText: "",
        options: ["", "", "", ""],
        correctAnswer: "",
        explanation: "",
      });

      const newQuestion = await res.json();
      setExistingQuestions((prev) => [...prev, newQuestion]);
    } catch (err) {
      console.error("Error adding question:", err);
      alert("Failed to add question. Please try again.");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#e0f7ff] via-[#f0fbff] to-[#f9fdff] py-10">
      <div className="max-w-4xl mx-auto px-4">
        <h1 className="text-3xl font-extrabold text-[#009fe3] mb-8">
          Manage Quiz Questions
        </h1>

        {existingQuestions.length > 0 && (
          <div className="mb-8 bg-white p-6 rounded-xl shadow">
            <h3 className="text-lg font-semibold mb-4 text-[#009fe3]">
              Existing Questions ({existingQuestions.length})
            </h3>
            <ol className="list-decimal list-inside space-y-3">
              {existingQuestions.map((q, index) => (
                <li key={q.id} className="text-gray-700">
                  {q.questionText}
                  <span className="ml-2 text-sm text-green-600">
                    (Correct: {q.correctAnswer})
                  </span>
                </li>
              ))}
            </ol>
          </div>
        )}

        <form onSubmit={handleAdd} className="bg-white p-8 rounded-xl shadow">
          <h2 className="text-xl font-bold mb-6">Add New Question</h2>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Question Text
              </label>
              <input
                type="text"
                placeholder="Enter the question"
                value={question.questionText}
                onChange={(e) =>
                  setQuestion((q) => ({ ...q, questionText: e.target.value }))
                }
                required
                className="w-full border p-2 rounded focus:ring-2 focus:ring-[#009fe3]"
              />
            </div>
            {question.options.map((opt, idx) => (
              <div key={idx}>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Option {idx + 1}
                </label>
                <input
                  type="text"
                  placeholder={`Option ${idx + 1}`}
                  value={opt}
                  onChange={(e) => handleOptionChange(idx, e.target.value)}
                  required
                  className="w-full border p-2 rounded focus:ring-2 focus:ring-[#009fe3]"
                />
              </div>
            ))}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Correct Answer
              </label>
              <select
                value={question.correctAnswer}
                onChange={(e) =>
                  setQuestion((q) => ({
                    ...q,
                    correctAnswer: e.target.value,
                  }))
                }
                required
                className="w-full border p-2 rounded focus:ring-2 focus:ring-[#009fe3]"
              >
                <option value="">Select Correct Answer</option>
                {question.options.map((opt, idx) => (
                  <option key={idx} value={opt}>
                    {opt || `Option ${idx + 1}`}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Explanation (optional)
              </label>
              <textarea
                placeholder="Add explanation for the correct answer"
                value={question.explanation}
                onChange={(e) =>
                  setQuestion((q) => ({
                    ...q,
                    explanation: e.target.value,
                  }))
                }
                className="w-full border p-2 rounded focus:ring-2 focus:ring-[#009fe3]"
                rows={3}
              />
            </div>
            <div className="flex items-center justify-between pt-6">
              <span className="text-sm text-gray-500">
                {20 - count} questions remaining
              </span>

              {/* Added Cancel Button */}
              <div className="flex space-x-4">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-2 rounded-lg font-semibold shadow-lg transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bg-[#009fe3] hover:bg-[#007cb5] text-white px-6 py-2 rounded-lg font-semibold shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={count >= 20}
                >
                  {count >= 20 ? "Quiz Full" : "Add Question"}
                </button>
              </div>
            </div>
            {count >= 20 && (
              <div className="mt-4 p-3 bg-green-100 text-green-700 rounded-lg">
                Quiz is complete! You can now assign it to students.
              </div>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
